import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { campaignsApi, optimizationApi } from '@/services/api';
import { features } from '@/config/features';
import { debug } from '@/utils/debug';
import type { Campaign } from '@/types/campaign';
import type { AppConfiguration, AppCampaignConfig } from '@/types/api';
import type { MetaAdSet } from '@/types/metaCampaign';
import { meaningfulStatus } from '@/utils/formatEnum';

/**
 * Helper function to merge API campaign data with app_configuration.
 * This ensures we get correct campaign_type and structure (asset_group_list, etc.)
 * even when the API returns raw Google Ads data.
 */
export function mergeCampaignWithConfig(
  apiCampaign: Campaign,
  configCampaign: AppCampaignConfig | undefined
): Campaign {
  if (!configCampaign) {
    return apiCampaign;
  }

  // Determine campaign type - prefer config, fallback to API
  let finalCampaignType = configCampaign.campaign_type || apiCampaign.campaign_type;

  // If we have asset_group_list with items but type doesn't include PERFORMANCE_MAX, fix it
  if (configCampaign.asset_group_list && configCampaign.asset_group_list.length > 0 &&
      !finalCampaignType?.includes('PERFORMANCE_MAX')) {
    finalCampaignType = 'PERFORMANCE_MAX';
    debug.log(`mergeCampaignWithConfig: Corrected campaign_type to PERFORMANCE_MAX based on asset_group_list presence`);
  }

  // Merge: API metrics + config structure/type
  // Note: Config types (e.g., AdGroupConfig) are compatible with Campaign types at runtime
  // but have slightly different TypeScript definitions. We use type assertion here since
  // we know the data structures are compatible.
  return {
    ...apiCampaign,
    // Use status from config if available (for optimistic updates persistence).
    // `campaign_status` is what the backend writes at the campaign level
    // (`_update_campaign_status_in_config`, `account_configuration.py`, Meta's
    // equivalent in meta_campaigns.py). `configCampaign.status` is a legacy field
    // an older frontend `saveConfiguration` used to write; if both are present,
    // `campaign_status` is authoritative. `meaningfulStatus` treats sentinels
    // ('UNKNOWN' / 'UNSPECIFIED') as missing so a regenerated `campaign_status:
    // 'UNKNOWN'` doesn't override a valid legacy `status` or live `apiCampaign.status`.
    status: (meaningfulStatus(configCampaign.campaign_status)
      || meaningfulStatus(configCampaign.status)
      || apiCampaign.status) as Campaign['status'],
    ads_group_list: configCampaign.ads_group_list as Campaign['ads_group_list'],
    asset_group_list: configCampaign.asset_group_list as Campaign['asset_group_list'],
    landing_pages: configCampaign.landing_pages as Campaign['landing_pages'],
    campaign_type: finalCampaignType,
    google_ads_account_id: configCampaign.google_ads_account_id || apiCampaign.google_ads_account_id,
    flouri_optimize_budget: configCampaign.flouri_optimize_budget,
    budget: configCampaign.budget || apiCampaign.budget,
    // Include campaign-level assets from app_configuration
    sitelinks: configCampaign.sitelinks as Campaign['sitelinks'],
    callouts: configCampaign.callouts as Campaign['callouts'],
    structured_snippets: configCampaign.structured_snippets as Campaign['structured_snippets'],
    // Include negative keywords from app_configuration
    negative_keywords: configCampaign.negative_keywords as Campaign['negative_keywords'],
    // Include audience targeting from app_configuration (for Demand Gen campaigns)
    audience_targeting: configCampaign.audience_targeting as Campaign['audience_targeting'],
    // Include network settings, location targeting, and language targeting
    network_settings: configCampaign.network_settings as Campaign['network_settings'],
    location_targeting: configCampaign.location_targeting as Campaign['location_targeting'],
    language_targeting: configCampaign.language_targeting as Campaign['language_targeting'],
  };
}

/**
 * Build a `Campaign`-shaped object from a Meta `AppCampaignConfig` row
 * in the unified saved config. Meta entries already share Google's
 * top-level metric field names (the backend constructor maps
 * `purchases_value → conversions_value`), so the shape is largely
 * additive — we surface the Meta-specific `ad_sets`, `objective`,
 * `landing_pages`, etc. without renaming anything.
 */
export function buildMetaCampaignFromConfig(
  configCampaign: AppCampaignConfig,
): Campaign {
  return {
    campaign_id: String(configCampaign.campaign_id),
    campaign_name: configCampaign.campaign_name ?? '',
    // Default to Meta's "running" status (`ACTIVE`) when both shape
    // sources are missing — this builder only runs for Meta campaigns,
    // so falling back to Google's `ENABLED` would tag a Meta row with a
    // vocabulary value `CampaignStatusBadge` doesn't expect from a
    // Meta entry and could confuse downstream color/label logic.
    // `meaningfulStatus` (not `??`) so a sentinel `campaign_status: 'UNKNOWN'`
    // falls through to legacy `status`, then to the 'ACTIVE' default — Meta's
    // running-state sentinel — rather than rendering an 'UNKNOWN' badge.
    status: (meaningfulStatus(configCampaign.campaign_status)
      ?? meaningfulStatus(configCampaign.status)
      ?? 'ACTIVE') as Campaign['status'],
    campaign_status: configCampaign.campaign_status,
    campaign_type: configCampaign.campaign_type ?? 'Meta Ads',
    budget: configCampaign.budget ?? 0,
    bidding_strategy: configCampaign.bid_strategy ?? '',
    impressions: configCampaign.impressions ?? 0,
    clicks: configCampaign.clicks ?? 0,
    conversions: configCampaign.conversions ?? 0,
    conversions_value: configCampaign.conversions_value ?? 0,
    cost: configCampaign.cost ?? 0,
    ctr: configCampaign.ctr ?? 0,
    conversion_rate: configCampaign.conversion_rate ?? 0,
    cpc: configCampaign.cpc ?? 0,
    cpa: configCampaign.cpa ?? 0,
    // 7-day window aggregates — the Campaign detail page's
    // CampaignMetrics card reads these straight off the merged
    // Campaign object. Without them every Meta campaign's "7-Day Avg"
    // column reads 0 while "28-Day Avg" shows real numbers.
    impressions_7d: configCampaign.impressions_7d,
    clicks_7d: configCampaign.clicks_7d,
    conversions_7d: configCampaign.conversions_7d,
    conversions_value_7d: configCampaign.conversions_value_7d,
    cost_7d: configCampaign.cost_7d,
    ctr_7d: configCampaign.ctr_7d,
    conversion_rate_7d: configCampaign.conversion_rate_7d,
    cpc_7d: configCampaign.cpc_7d,
    cpa_7d: configCampaign.cpa_7d,
    created_at: '',
    updated_at: '',
    flouri_optimize_budget: configCampaign.flouri_optimize_budget,
    landing_pages: configCampaign.landing_pages as Campaign['landing_pages'],
    // Platform discriminator + Meta-specific
    platform_source: 'Meta Ads',
    ad_sets: configCampaign.ad_sets as MetaAdSet[] | undefined,
    objective: configCampaign.objective,
    meta_ad_account_id: configCampaign.meta_ad_account_id,
    buying_type: configCampaign.buying_type,
    is_cbo: configCampaign.is_cbo,
    daily_budget: configCampaign.daily_budget,
    lifetime_budget: configCampaign.lifetime_budget,
    special_ad_categories: configCampaign.special_ad_categories,
    bid_strategy: configCampaign.bid_strategy,
    start_time: configCampaign.start_time,
    stop_time: configCampaign.stop_time,
    reach: configCampaign.reach,
    cpm: configCampaign.cpm,
    purchases: configCampaign.purchases,
    purchases_value: configCampaign.purchases_value,
  };
}

/**
 * Hook to get campaign data from cache (campaigns + app config) or fallback to API.
 *
 * In refresh_on_login mode:
 * - Tries to derive campaign from already-cached campaigns list + app config
 * - Only makes API call if cache miss
 *
 * In other modes:
 * - Falls back to API call (normal behavior)
 *
 * Meta campaigns are read straight from the unified saved app_configuration row
 * (the backend constructor at `account_configuration.py:2196` already produces
 * the Campaign shape with `platform_source='Meta Ads'`). No live `/meta/campaigns/{id}`
 * merge is performed because writes are A4 — saved-config is the SOT for reads.
 */
export function useCampaignFromCache(campaignId: string) {
  const { isAuthenticated, memberUuid } = useAuthStore();
  const queryClient = useQueryClient();
  const freshness = features.campaignDataFreshness;

  return useQuery({
    queryKey: ['campaign-from-cache', campaignId],
    queryFn: async () => {
      // For refresh_on_login mode, try to derive from cache first
      if (freshness === 'refresh_on_login') {
        try {
          // Get campaigns from cache (prefetched during login)
          const campaignsCache = queryClient.getQueryData<Campaign[]>(['campaigns', false]);

          // Get app config from cache (prefetched during login)
          const appConfigCache = queryClient.getQueryData<AppConfiguration>(['app-config', memberUuid]);

          if (campaignsCache && appConfigCache?.configuration?.campaign_list) {
            // Find campaign in cached campaigns list
            const campaignWithMetrics = campaignsCache.find(c => String(c.campaign_id) === String(campaignId));

            if (campaignWithMetrics) {
              // Find campaign structure in app config
              const campaignConfig = appConfigCache.configuration.campaign_list.find(
                (c) => String(c.campaign_id) === String(campaignId)
              );

              if (campaignConfig) {
                // Use shared helper to merge API metrics + config structure/type
                const merged = mergeCampaignWithConfig(campaignWithMetrics, campaignConfig);

                debug.log(`useCampaignFromCache: Derived campaign ${campaignId} from cache`, {
                  has_ad_groups: !!merged.ads_group_list,
                  ad_groups_count: merged.ads_group_list?.length,
                  has_asset_groups: !!merged.asset_group_list,
                  asset_groups_count: merged.asset_group_list?.length,
                  campaign_type: merged.campaign_type,
                  config_campaign_type: campaignConfig.campaign_type,
                  metrics_campaign_type: campaignWithMetrics.campaign_type,
                });

                return merged;
              } else {
                debug.log(`useCampaignFromCache: Campaign ${campaignId} config not found, using metrics only`);
                return campaignWithMetrics;
              }
            } else {
              debug.log(`useCampaignFromCache: Campaign ${campaignId} not found in campaigns cache`);
            }
          } else {
            debug.log(`useCampaignFromCache: Cache miss for campaign ${campaignId}`, {
              hasCampaigns: !!campaignsCache,
              hasAppConfig: !!appConfigCache,
            });
          }
        } catch (error) {
          debug.error(`useCampaignFromCache: Error deriving campaign from cache`, error);
        }
      }

      // Check whether this campaignId corresponds to a Meta entry in the
      // saved config — if so, resolve from app_configuration directly and
      // skip the Google `/campaigns/{id}` API path entirely. The Meta
      // entry's shape (set by the backend Meta constructor) is already
      // Campaign-compatible.
      //
      // Reuse the existing `['app-config', memberUuid]` React Query
      // cache that `useAppConfigQuery` populates — prefetched during
      // login and refreshed by the Account page. Falling back to
      // `queryClient.fetchQuery` with the same key dedupes against
      // any in-flight fetch and seeds the cache for subsequent
      // readers so we don't fire a parallel `loadConfiguration` call
      // here.
      if (memberUuid) {
        try {
          const appConfigKey = ['app-config', memberUuid];
          let appConfig = queryClient.getQueryData<AppConfiguration>(appConfigKey);
          if (!appConfig) {
            appConfig = await queryClient.fetchQuery<AppConfiguration>({
              queryKey: appConfigKey,
              queryFn: () => optimizationApi.loadConfiguration(memberUuid),
            });
          }
          const configCampaign = appConfig?.configuration?.campaign_list?.find(
            (c: AppCampaignConfig) => String(c.campaign_id) === String(campaignId)
          );
          if (configCampaign?.platform_source === 'Meta Ads') {
            const meta = buildMetaCampaignFromConfig(configCampaign);
            debug.log(`useCampaignFromCache: Resolved Meta campaign ${campaignId} from saved config`, {
              ad_sets_count: meta.ad_sets?.length ?? 0,
              has_landing_pages: !!meta.landing_pages?.length,
            });
            return meta;
          }
        } catch (metaCheckError) {
          debug.warn(`useCampaignFromCache: Meta check failed, falling through to Google path`, metaCheckError);
        }
      }

      // Fallback: fetch from API with use_cached=false to get fresh metrics
      // use_cached=true only returns structure from app_configuration, not metrics
      debug.log(`useCampaignFromCache: Fetching campaign ${campaignId} from API (use_cached=false)`);

      // Fetch campaign from API with fresh metrics
      // Try use_cached=false first for freshness, but fall back to use_cached=true
      // if that fails (e.g., 404 for new campaigns without metrics data)
      let apiCampaign;
      try {
        apiCampaign = await campaignsApi.getCampaign(campaignId, false);
      } catch (freshError) {
        debug.warn(`useCampaignFromCache: Fresh fetch failed, trying cached`, freshError);
        apiCampaign = await campaignsApi.getCampaign(campaignId, true);
      }

      // CRITICAL: Also fetch app_configuration to get correct campaign_type and structure
      // The API returns campaign_type from Google Ads data tables, which may be incorrect
      // (e.g., SEARCH instead of PERFORMANCE_MAX). app_configuration has the correct type.
      try {
        if (memberUuid) {
          const appConfig = await optimizationApi.loadConfiguration(memberUuid);
          const configCampaign = appConfig?.configuration?.campaign_list?.find(
            (c: AppCampaignConfig) => String(c.campaign_id) === String(campaignId)
          );

          if (configCampaign) {
            const merged = mergeCampaignWithConfig(apiCampaign, configCampaign);
            debug.log(`useCampaignFromCache: Merged API campaign with app_configuration`, {
              api_campaign_type: apiCampaign.campaign_type,
              config_campaign_type: configCampaign.campaign_type,
              final_campaign_type: merged.campaign_type,
              has_asset_groups: !!merged.asset_group_list,
              asset_groups_count: merged.asset_group_list?.length || 0,
            });
            return merged;
          }
        }
      } catch (configError) {
        debug.warn(`useCampaignFromCache: Failed to fetch app_configuration for merge`, configError);
        // Fall through to return unmerged API campaign
      }

      return apiCampaign;
    },
    enabled: isAuthenticated && !!campaignId,
    staleTime: freshness === 'refresh_on_login' ? Infinity : 0,
    gcTime: 10 * 60 * 1000,
    retry: 2,
  });
}
